import axios from "axios";

/**
 * GET /account/referral payload (kitecorp/transferify-api-service#433).
 *
 * Counts and money come from the server, never from arithmetic here: the browser cannot see another
 * account's payment state, and a client-side tally would drift the moment a vesting webhook lands while the
 * page is open.
 *
 * This DTO carries no {@code AssertNoSpecDrift} binding yet — `src/api/schema.d.ts` is generated from the
 * committed snapshot of the DEPLOYED api spec, so the binding can only be added once the api side of #433
 * has deployed and the schema-sync automation has refreshed that snapshot. Referencing a schema the
 * snapshot does not yet contain would fail `tsc -b` on every build until then.
 */
export type ReferralSummary = {
	/** The account's stable share code. */
	code: string;
	/** Referred accounts that have not paid yet. */
	pendingCount: number;
	/** Referred accounts whose first payment banked a reward. */
	vestedCount: number;
	/** Total credit earned, in MINOR currency units. */
	earnedCreditMinor: number;
	/** Lowercase ISO-4217 for {@link earnedCreditMinor}; null until something vests. */
	earnedCreditCurrency: string | null;
	/** Rewards still available inside the rolling year. */
	remainingThisYear: number;
	/** How long a first-touch attribution stays valid client-side, in days. */
	attributionWindowDays: number;
};

/** The signed-in account's referral card. Issues the code server-side on first read. */
export async function fetchReferralSummary(): Promise<ReferralSummary> {
	const response = await axios.get<ReferralSummary>("/account/referral");
	return response.data;
}

/**
 * Applies a referral code to the signed-in account. Answers 204; every refusal is a localized 4xx the
 * caller must surface rather than swallow — a client that stored a code needs to know whether to clear it.
 */
export async function submitReferralCode(code: string): Promise<void> {
	await axios.post("/account/referral", {code});
}
